let _apiUrl = '';
const COOKIE_NAME = 'tranc';
const FLUSH_INTERVAL = 5000;
const MAX_BATCH_SIZE = 10;
const ANONYMOUS_ID_KEY = '_a_id';
const SESSION_ID_KEY = '_a_sid';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getOrCreate = (storage, key) => {
  try {
    let id = storage.getItem(key);
    if (!id) {
      id = generateUUID();
      storage.setItem(key, id);
    }
    return id;
  } catch {
    return generateUUID();
  }
};

const detectDevice = () => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
};

const detectBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

// ─── Singleton State ──────────────────────────────────────────────────────────

let _initialized = false;
let _buffer = [];
let _flushing = false;
let _lastPageView = null;
let _anonymousId = null;
let _sessionId = null;
let _dispositivo = null;
let _navegador = null;
let _origen = null;

// ─── Core ─────────────────────────────────────────────────────────────────────

const _addEvent = (eventData) => {
  if (!_initialized) return;

  _buffer.push({
    anonymous_id: _anonymousId,
    session_id: _sessionId,
    dispositivo: _dispositivo,
    navegador: _navegador,
    origen: _origen,
    ...eventData,
  });

  if (_buffer.length >= MAX_BATCH_SIZE) {
    flush();
  }
};

const _sendBatch = async (events) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getCookie(COOKIE_NAME);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${_apiUrl}/analiticas`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ events }),
    keepalive: true,
  });

  if (!response.ok) {
    throw new Error(`Analytics API error: ${response.status}`);
  }
};

// ─── Public API ───────────────────────────────────────────────────────────────

const flush = () => {
  if (_buffer.length === 0 || _flushing) return;

  _flushing = true;
  const events = [..._buffer];
  _buffer = [];

  _sendBatch(events)
    .catch(err => console.error('Analytics flush error:', err))
    .finally(() => { _flushing = false; });
};

const flushSync = () => {
  if (_buffer.length === 0) return;

  const events = [..._buffer];
  _buffer = [];

  const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
  navigator.sendBeacon(`${_apiUrl}/analiticas`, blob);
};

/**
 * Track a click event.
 * @param {string} elemento - Element identifier (e.g. 'btn-login')
 * @param {string} [ruta] - Page path (defaults to current pathname)
 */
const trackClick = (elemento, ruta) => {
  _addEvent({
    evento: 'click',
    elemento,
    ruta: ruta || window.location.pathname,
    referrer: document.referrer || '',
  });
};

/**
 * Track a page view event.
 * @param {string} [ruta] - Page path
 * @param {string} [referrer] - Referring page
 */
const trackPageView = (ruta, referrer) => {
  const currentPath = ruta || window.location.pathname;
  if (_lastPageView === currentPath) return;
  _lastPageView = currentPath;

  _addEvent({
    evento: 'pageview',
    ruta: currentPath,
    referrer: referrer || document.referrer || '',
  });
};

// ─── Auto-initialization ──────────────────────────────────────────────────────

const _init = () => {
  if (_initialized) return;
  if (typeof window === 'undefined') return;

  _anonymousId = getOrCreate(localStorage, ANONYMOUS_ID_KEY);
  _sessionId = getOrCreate(sessionStorage, SESSION_ID_KEY);
  _dispositivo = detectDevice();
  _navegador = detectBrowser();
  _origen = window.location.origin;
  _initialized = true;

  setInterval(flush, FLUSH_INTERVAL);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushSync();
    }
  });

  trackPageView();

  const originalPushState = window.history.pushState;
  window.history.pushState = function (...args) {
    originalPushState.apply(this, args);
    trackPageView();
  };

  window.addEventListener('popstate', () => trackPageView());

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-track-click]');
    if (el) {
      trackClick(el.getAttribute('data-track-click'));
    }
  });
};

/**
 * Initialize the analytics library.
 * Call this once at the root of your app (e.g. App.js).
 * @param {string} apiUrl - The base API URL (e.g. 'https://dto-api-qa.vwmapps.com.mx/api-dev')
 */
const init = (apiUrl) => {
  if (!apiUrl) {
    console.warn('Analytics: apiUrl is required. Analytics disabled.');
    return;
  }
  _apiUrl = apiUrl.replace(/\/$/, ''); // Remove trailing slash if present
  _init();
};

// ─── Exports ──────────────────────────────────────────────────────────────────

export const analytics = {
  init,
  trackClick,
  trackPageView,
  flush,
  flushSync,
};

export default analytics;

